import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { getAutoStoryboardProgress, cancelAutoStoryboard } from "@/services/shotService";
import { Button } from "@/components/ui/button";
import { Loader2, X, CheckCircle2, AlertTriangle, Film } from "lucide-react";
import DirectorMonitor from "@/components/DirectorMonitor";

const STAGE_TO_STEP = {
  preflight: "prepare",
  stage_1: "analyze",
  stage_2: "prompts",
  stage_3: "render",
  completed: "render",
  failed: "render",
  cancelled: "render",
};

const STEPS = [
  { key: "prepare", label: "Preparing" },
  { key: "analyze", label: "Script Breakdown" },
  { key: "prompts", label: "Visual Prompts" },
  { key: "render", label: "Rendering Frames" },
];

const COMMENTARY = {
  preflight: "Setting up the shot list...",
  stage_1: "Claude is breaking down the script into shot anchors...",
  stage_2: "Crafting visual prompts with camera angles and lighting...",
  stage_3: "Gemini is rendering frames...",
  completed: "All frames rendered!",
  failed: "Generation encountered an error.",
  cancelled: "Generation was cancelled.",
};

export default function AutoStoryboardProgress({ projectId, onComplete, onDismiss, inline = false }) {
  const [progress, setProgress] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!projectId) return;

    const poll = async () => {
      try {
        const data = await getAutoStoryboardProgress(projectId);
        setProgress(data);

        if (data.status === "completed" || data.status === "failed" || data.status === "cancelled") {
          if (intervalRef.current) clearInterval(intervalRef.current);
          if (data.status === "completed" && onComplete) {
            setTimeout(() => onComplete(), 2500);
          }
        }
      } catch (err) {
        if (intervalRef.current) clearInterval(intervalRef.current);
        setProgress(null);
      }
    };

    poll();
    intervalRef.current = setInterval(poll, 3000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [projectId]);

  const handleCancel = async () => {
    setCancelling(true);
    try { await cancelAutoStoryboard(projectId); } catch (err) { console.error("Cancel failed:", err); }
    setCancelling(false);
  };

  if (!progress) return null;

  const isActive = ["preflight", "stage_1", "stage_2", "stage_3"].includes(progress.status);
  const sp = progress.current_scene_progress || {};

  // Build dynamic commentary
  let commentary = COMMENTARY[progress.status] || progress.status;
  if (isActive && progress.total_scenes > 1) {
    commentary = `Scene ${progress.current_scene}/${progress.total_scenes} — ${commentary}`;
  }
  if (progress.status === "stage_3" && sp.total_frames > 0) {
    commentary = `Rendering frame ${sp.frames_completed + 1} of ${sp.total_frames}...`;
    if (progress.total_scenes > 1) {
      commentary = `Scene ${progress.current_scene}/${progress.total_scenes} — ${commentary}`;
    }
  }

  const status = isActive ? "active"
    : progress.status === "completed" ? "completed"
    : progress.status === "failed" ? "failed"
    : "cancelled";

  if (inline) {
    // Inline mode — rendered inside StoryboardPage
    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-xl border border-[var(--border-default)] bg-[var(--bg-surface)] overflow-hidden"
      >
        <div className="flex gap-4">
          {/* Progress section */}
          <div className="flex-1 p-4">
            <DirectorMonitor
              steps={STEPS}
              currentStep={STAGE_TO_STEP[progress.status] || "prepare"}
              status={status}
              commentary={commentary}
              errorMessage={progress.error_message || ""}
              quoteContext="shots"
              onDismiss={!isActive ? onDismiss : undefined}
              compact={false}
            />
          </div>

          {/* Frame preview + stats sidebar */}
          <div className="w-72 border-l border-[var(--border-subtle)] flex flex-col">
            {progress.last_frame_image && (
              <div className="relative">
                <img
                  src={`data:image/png;base64,${progress.last_frame_image}`}
                  alt="Latest frame"
                  className="w-full h-44 object-cover"
                />
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                  <div className="flex items-center gap-1.5 text-white">
                    <Film size={11} />
                    <span className="text-[10px]">
                      {(progress.overall_progress?.frames_completed || 0)} frames rendered
                      {sp.frames_failed > 0 && ` · ${sp.frames_failed} failed`}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Stats */}
            <div className="p-3 space-y-2 text-xs text-[var(--text-muted)]">
              {progress.total_scenes > 0 && (
                <div className="flex justify-between">
                  <span>Scenes</span>
                  <span className="text-[var(--text-primary)] font-medium">{progress.scenes_completed || 0} / {progress.total_scenes}</span>
                </div>
              )}
              {(progress.overall_progress?.total_frames_all_scenes || 0) > 0 && (
                <div className="flex justify-between">
                  <span>Frames</span>
                  <span className="text-[var(--text-primary)] font-medium">
                    {progress.overall_progress?.frames_completed || 0} / {progress.overall_progress?.total_frames_all_scenes || 0}
                  </span>
                </div>
              )}
              {(progress.overall_progress?.percent_complete || 0) > 0 && (
                <div className="w-full h-1.5 bg-[var(--bg-muted)] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[var(--color-gold-500)] rounded-full transition-all duration-500"
                    style={{ width: `${progress.overall_progress.percent_complete}%` }}
                  />
                </div>
              )}
            </div>

            {/* Cancel button */}
            {isActive && progress.can_cancel && (
              <div className="p-3 pt-0 mt-auto">
                <Button
                  onClick={handleCancel}
                  variant="outline"
                  size="sm"
                  className="w-full text-xs h-7"
                  disabled={cancelling}
                >
                  {cancelling ? <Loader2 size={11} className="animate-spin mr-1" /> : <X size={11} className="mr-1" />}
                  Cancel Generation
                </Button>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    );
  }

  // Floating compact mode — shown when NOT on storyboard tab
  return (
    <AnimatePresence>
      <div>
        <DirectorMonitor
          steps={STEPS}
          currentStep={STAGE_TO_STEP[progress.status] || "prepare"}
          status={status}
          commentary={commentary}
          errorMessage={progress.error_message || ""}
          quoteContext="shots"
          onDismiss={!isActive ? onDismiss : undefined}
          compact={true}
        />

        {/* Frame preview + cancel overlay */}
        {(progress.last_frame_image || (isActive && progress.can_cancel)) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="fixed bottom-6 right-[460px] z-50 w-64 rounded-xl border border-[var(--border-default)] bg-[var(--bg-surface)] shadow-2xl overflow-hidden"
          >
            {/* Latest frame preview */}
            {progress.last_frame_image && (
              <div className="relative">
                <img
                  src={`data:image/png;base64,${progress.last_frame_image}`}
                  alt="Latest frame"
                  className="w-full h-40 object-cover"
                />
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                  <div className="flex items-center gap-1.5 text-white">
                    <Film size={11} />
                    <span className="text-[10px]">
                      {(progress.overall_progress?.frames_completed || 0)} frames
                      {sp.frames_failed > 0 && ` (${sp.frames_failed} failed)`}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Cancel button */}
            {isActive && progress.can_cancel && (
              <div className="p-2">
                <Button
                  onClick={handleCancel}
                  variant="outline"
                  size="sm"
                  className="w-full text-xs h-7"
                  disabled={cancelling}
                >
                  {cancelling ? <Loader2 size={11} className="animate-spin mr-1" /> : <X size={11} className="mr-1" />}
                  Cancel
                </Button>
              </div>
            )}
          </motion.div>
        )}
      </div>
    </AnimatePresence>
  );
}
